import {
  Box,
  Flex,
  FlexProps,
  Grid,
  Heading,
  mdBumps,
  TertiaryButton,
  Typo,
} from '@wrisk/ui-components'
import { negate } from 'lodash-es'
import { DateTime } from 'luxon'
import React, { FunctionComponent } from 'react'

import { getAdditionalProfiles, Quote } from '../../../domain'
import { useQuoteData } from '../../../hooks/adjustments/quote'
import {
  tFormats,
  TKey,
  useWriskTranslation,
} from '../../../infrastructure/internationalisation'
import { useAdjustments } from '../../../state'
import { isDateTimeToday } from '../../../util/date'
import { FormFormatter, isAnsweredInput } from '../../formBuilder'
import { isHiddenConfig, isInputConfig } from '../../formBuilder/config'
import { useProduct } from '../../product/ProductProvider'

const tKey = TKey('components.quote-details')

export interface QuoteDetailsProps extends FlexProps {
  onEdit: () => void
  quote: Quote
  startAt?: DateTime
  endAt?: DateTime
}

export const QuoteDetails: FunctionComponent<QuoteDetailsProps> = ({
  quote,
  startAt,
  endAt,
  onEdit,
  ...props
}) => {
  const { t } = useWriskTranslation()

  const { getAdjustment } = useAdjustments()

  const {
    product: { quoteDetails },
  } = useProduct()

  const toData = useQuoteData(quote)

  const formConfig = quoteDetails
    ?.filter(isInputConfig)
    .filter(negate(isHiddenConfig))
    .filter((input) => {
      const adjustment = getAdjustment(input.name)
      const value = toData(adjustment)
      return isAnsweredInput(value)
    })

  const additionalProfiles = getAdditionalProfiles(quote)

  const additionalProfileSection = additionalProfiles.length
    ? [
        <Typo key='additional-profiles-header' typoSize='sm' color='bodySecondary'>
          {t(tKey('additionalProfile.title'))}
        </Typo>,
        <Box key='additional-profiles-value'>
          {additionalProfiles.map((profile) => (
            <Typo key={profile.profileCode} typoSize='sm'>
              {t(tKey('additionalProfile.detail'), { profile })}
            </Typo>
          ))}
        </Box>,
      ]
    : null

  const startAtSection = startAt
    ? [
        <Typo key='start-at-header' typoSize='sm' color='bodySecondary'>
          {t(tKey('start-at.title'))}
        </Typo>,
        <Typo key='start-at-value' typoSize='sm'>
          {startAt
            ? isDateTimeToday(startAt)
              ? t(tKey('start-at.today'))
              : t(tFormats('datetime.medium'), { value: startAt })
            : null}
        </Typo>,
      ]
    : null

  const endAtSection = endAt
    ? [
        <Typo key='end-at-header' typoSize='sm' color='bodySecondary'>
          {t(tKey('end-at.title'))}
        </Typo>,
        <Typo key='end-at-value' typoSize='sm'>
          {t(tFormats('datetime.medium'), { value: endAt })}
        </Typo>,
      ]
    : null

  const updateDetailsButton = (
    <TertiaryButton layout='small' onClick={onEdit} data-testid='update-details-button'>
      {t(tKey('actions.edit'))}
    </TertiaryButton>
  )

  return (
    <Flex width={1} flexDirection='column' variant='raised' {...props}>
      <Flex width={1} px={mdBumps} pt={mdBumps} pb={1}>
        <Heading variant='h3' typoSize='md'>
          {t(tKey('header'))}
        </Heading>
        {updateDetailsButton}
      </Flex>

      <Grid
        width={1}
        justifyItems='start'
        gridTemplateColumns={['0.5fr 0.5fr', '0.5fr 0.5fr', '0.33fr 0.66fr']}
        rowGap={2}
        columnGap={2}
        px={mdBumps}
        py={5}
      >
        {formConfig?.map((input) => [
          <Typo key={`${input.name}-header`} typoSize='sm' color='bodySecondary'>
            {t(tKey('titles', input.name))}
          </Typo>,
          <Typo key={`${input.name}-value`} typoSize='sm'>
            <FormFormatter
              input={input}
              size='small'
              value={toData(getAdjustment(input.name))}
              t={t}
            />
          </Typo>,
        ])}

        {additionalProfileSection}
        {startAtSection}
        {endAtSection}
      </Grid>
    </Flex>
  )
}
